import { useCallback, useMemo, useRef, useState } from 'react';
import { Form } from 'ALIASED-antd';
import { isFunction } from 'lodash';
import { SessionForm } from '@components/Clinicians/session-form';
import ClinicianSessionFormState from '@components/Clinicians/Session/hooks/clinicianSessionFormState';
import { useSessionAppointmentId } from './useSessionAppointmentId';

export function useSessionFormState<K extends keyof SessionForm, T extends SessionForm[K]>(key: K, initialValue?: T) {
  const appointment_id = useSessionAppointmentId(true);
  const storageKey = useMemo(() => `appointment[${appointment_id}].${key}` as const, [appointment_id, key]);
  const form = Form.useFormInstance<SessionForm>();
  const formValue = Form.useWatch(key, { form, preserve: true });

  useState(() => {
    const value = ClinicianSessionFormState.get<T>(storageKey) ?? initialValue;

    if (value !== undefined && value !== null) {
      form.setFieldValue(key, value);
      ClinicianSessionFormState.set(storageKey, value);
    }

    return value;
  });

  return [
    formValue,
    useCallback<React.Dispatch<React.SetStateAction<SessionForm[K] | undefined>>>(
      (v) => {
        const value = isFunction(v) ? v(form.getFieldValue(key)) : v;

        form.setFieldValue(key, value);
        ClinicianSessionFormState.set(storageKey, value);
      },
      [form, key, storageKey]
    )
  ] as const;
}

export function useCleanUpAppointmentStorage() {
  const appointment_id = useSessionAppointmentId(true);
  const isCleaned = useRef(false);

  return useCallback(() => {
    if (!appointment_id || isCleaned.current) return;
    ClinicianSessionFormState.clearAppointmentSessionData(appointment_id);
    isCleaned.current = true;
  }, [appointment_id]);
}
